import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import { fg } from '@confluence/feature-gating';

import { SpacePagesQuery } from './SpacePagesQuery.graphql.ts';
import { PagesSpaceIdQuery } from './hooks/PagesSpaceIdQuery.graphql.ts';
import { SpacePagesPersistenceQuery } from './SpacePagesPersistenceQueries.graphql.ts';
import {
	ConfluenceSearchContentStatus,
	ConfluenceContentSearchScope,
	ConfluenceContentSortingParameter as PagesSortField,
	SortOrder as PagesSortOrder,
} from './__types__/SpacePagesQuery.ts';
import type { PreloadSpacePagesOptions } from './types.ts';
import { SearchResultsAGGSearchQuery } from './SearchResultsAGGSearchQuery.ts';
import type {
	SearchResultsAGGSearchQuery as SearchResultsAGGSearchQuery$data,
	SearchResultsAGGSearchQueryVariables as SearchResultsAGGSearchQuery$variables,
} from './__types__/SearchResultsAGGSearchQuery.ts';
import { getPreloadAggSpacePagesQueryVariables, SPACE_PAGES_QUERY_LIMIT } from './utils/queryUtils.ts';

export const preloadSpacePages = (options: PreloadSpacePagesOptions) => {
	if (fg('cc-agg-migration_space-pages-to-agg')) {
		return preloadSpacePagesAgg(options);
	}
	return preloadSpacePagesCCGraph(options);
};

const preloadSpacePagesAgg = (options: PreloadSpacePagesOptions) => {
	const variables = getPreloadAggSpacePagesQueryVariables(options);
	const { spaceKey, isLoggedIn, spaceId = '' } = options;
	return waitForAllReturnWhenError([
		queryAgg<SearchResultsAGGSearchQuery$data, SearchResultsAGGSearchQuery$variables>({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			query: SearchResultsAGGSearchQuery,
			variables,
		}),
		...(!spaceId
			? [
					query({
						/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
						query: PagesSpaceIdQuery,
						variables: { spaceKey },
					}),
				]
			: []),
		...(isLoggedIn
			? [
					query({
						/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
						query: SpacePagesPersistenceQuery,
						variables: { spaceKey },
					}),
				]
			: []),
	]);
};

export const preloadSpacePagesCCGraph = ({
	spaceKey,
	queryParams,
	userId,
	isLicensed,
	isLoggedIn,
	spaceId = '',
	isWhiteboardEnabled = false,
	isDatabaseEnabled = false,
}: PreloadSpacePagesOptions) => {
	const pagesFilter = getSingleParam(queryParams, 'pagesFilter');
	const searchText = getSingleParam(queryParams, 'text') || '';
	const variables = {
		first: 24,
		maxNumberOfResults: SPACE_PAGES_QUERY_LIMIT,
		searchText,
		filters: {
			spaces: { spaceKeys: [spaceKey] },
			titleMatchOnly: { titleMatchOnly: true },
			...(pagesFilter === 'worked-on' &&
				userId &&
				isLicensed && {
					contributors: { accountIds: [userId] },
				}),
			...(pagesFilter === 'starred' &&
				isLicensed && {
					labels: [{ namespace: 'MY', name: 'favourite' }],
				}),
			statuses: {
				statuses: [ConfluenceSearchContentStatus.CURRENT, ConfluenceSearchContentStatus.DRAFT],
			},
		},
		sortBy: [
			{
				field: PagesSortField.LAST_MODIFIED_DATE,
				order: PagesSortOrder.DESC,
			},
		],
		isLicensed: Boolean(isLicensed),
		includeRecommended: false,
		spaceId,
		scopes: [
			ConfluenceContentSearchScope.PAGE,
			...(isWhiteboardEnabled ? [ConfluenceContentSearchScope.WHITEBOARD] : []),
			...(isDatabaseEnabled ? [ConfluenceContentSearchScope.DATABASE] : []),
			ConfluenceContentSearchScope.EMBED,
			ConfluenceContentSearchScope.FOLDER,
		],
	};

	return waitForAllReturnWhenError([
		query({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			query: SpacePagesQuery,
			variables,
		}),
		...(!spaceId
			? [
					query({
						/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
						query: PagesSpaceIdQuery,
						variables: { spaceKey },
					}),
				]
			: []),
		...(isLoggedIn
			? [
					query({
						/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
						query: SpacePagesPersistenceQuery,
						variables: { spaceKey },
					}),
				]
			: []),
	]);
};
